import React, { useState, useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Typography,
  Button,
  Card,
  // Slider,
  Grid,
  CircularProgress,
} from "@mui/material";
import Slider from "../../../../shared/utils/CustomRangeSlider";
import MappedStoresInCampaign from "./MappedStoresInCampaign";
import GlobalSnackbar from "../../../../shared/utils/GlobalSnackbar";
import GlobalButton from "../../../../shared/utils/GlobalButton";

const AssignDeliveryCenterInCampaign = () => {
  const [selectedCampaign, setSelectedCampaign] = React.useState({});
  const [distance, setDistance] = React.useState(10);
  const [locationsUploadedInCampaign, setLocationsUploadedInCampaign] =
    React.useState([]);
  const [batchesCreatedForDeliveryCenter, setBatchesCreatedForDeliveryCenter] =
    React.useState([]);
  const [jobsNotLinkedinBatches, setJobsLinkedInBatches] = React.useState([]);
  const [deliveryCentersNeeded, setDeliveryCentersNeeded] = React.useState([]);
  const [statesInCampaign, setStatesInCampaign] = React.useState([]);
  const [selectedMarker, setSelectedMarker] = React.useState(null);
  const [selectedDCId, setSelectedDCId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [savedBatchesCount, setSavedBatchesCount] = React.useState([0]); // New state for saved batches
  // ── Geocoding progress: null = not started, 0-100 = in progress, done = null
  const [geocodeProgress, setGeocodeProgress] = React.useState(null); // 0–100 | null
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const { id } = useParams();
  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");

  // ── Fetch RFP → extract locations → geocode all stores ───────────────────
  const handleSelectedCampaign = async () => {
    try {
      const response = await axios.get(
        `${serverURL}/campaign/getCampaignLocations?campaignId=${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success !== true) return;
      const { locations, locationsUploadedUpdatedAt, campaign } =
        response.data.data;
      setSelectedCampaign(campaign);
      if (!locations.length) return;
      // Derive states for the DC fetch (can start immediately with raw locations)
      const states = [
        ...new Set(locations.map((l) => l.details.state).filter(Boolean)),
      ];
      setStatesInCampaign(states);
      //   // Show raw locations in the table immediately — map pins come after geocoding
      //   setLocationsUploadedInCampaign(locations);
      //   // Step 2 — geocode every location in the background
      //   // The progress bar gives the user feedback during what can be a 10–30 s wait
      //   setGeocodeProgress(0);
      //   const geolocated = await geocodeLocations(locations, (done, total) => {
      //     setGeocodeProgress(Math.round((done / total) * 100));
      //   });
      //   setGeocodeProgress(null); // hide bar when done
      //   // Replace with the enriched version (has locationCoordinates on each item)
      setLocationsUploadedInCampaign(locations);
    } catch (error) {
      console.error(error);
      setGeocodeProgress(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch DCs for the states present in this enquiry ─────────────────────
  const fetchDeliveryCentersInUploadedStates = async () => {
    try {
      const response = await axios.get(
        `${serverURL}/deliveryCenter/viewAll?page=1&limit=500&states=${statesInCampaign}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response?.data?.success === true) {
        setDeliveryCentersNeeded(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Restore saved vendor batches from DB onto the frontend batch state ─────
  // Called after batchesCreatedForDeliveryCenter is populated (either from
  // handleCreateBatches or on page load).
  //
  // The DB stores raw store objects in storesAllocated[].  We can't restore
  // object references (new Excel parse = new refs), so we match stores by the
  // composite key (storeName + city) and rebuild vendorBatches using the live
  // references from the freshly-geocoded locationsUploadedInCampaign array.
  // This means drag-drop, reference-equality dedup, and map pins all keep working.
  const restoreSavedBatches = React.useCallback(
    async (freshBatches, geolocatedStores) => {
      try {
        const res = await axios.get(
          `${serverURL}/deliveryCenter/${id}/dc-batches`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.data?.success || !res.data.data?.length) return freshBatches;

        // ── Key: storeOwnerPhoneNumber (unique per store instance) ────────────
        // storeName+city is NOT unique — the DB data confirms "Reliance Smart"
        // in "New Delhi" appears across VB-1 and VB-2 (different phone numbers).
        // Phone number is always present in every saved storesAllocated entry.
        // const jobIdRef = new Map(); // jobId → live store reference
        const storeRefMap = new Map(); // phoneNumber → live store reference
        for (const store of geolocatedStores) {
          const phone = String(store.details?.storeOwnerPhoneNumber ?? "");
          // const jobId = String(store.details?.jobId ?? "");
          // if (jobId && !jobIdRef.has(jobId)) {
          //   jobIdRef.set(jobId, store);
          // }
          if (phone && !storeRefMap.has(phone)) {
            storeRefMap.set(phone, store);
          }
        }

        // ── Parse saved rows into per-DC buckets ──────────────────────────────
        // res.data.data = [{ deliveryCenterCity, batches: [rowDoc, ...] }]
        //
        // Two kinds of rows:
        //   vendorId = null  → stores belong directly to the DC (dc.stores),
        //                       not to a vendor sub-batch
        //   vendorId = {...} → stores belong to a vendor sub-batch (vendorBatches)
        //
        // Within each DC we group by vendorId so multiple cityLinked rows for the
        // same vendor get merged into a single vendorBatch entry.
        const savedByCity = new Map();
        // Structure: cityName → { dcStores: Set<liveRef>, vendorMap: Map<vId, vbEntry> }

        for (const group of res.data.data) {
          const city = group.deliveryCenterCity;
          if (!savedByCity.has(city)) {
            savedByCity.set(city, {
              dcStores: [],
              dcBatch: null,
              vendorMap: new Map(),
            });
          }
          const bucket = savedByCity.get(city);

          for (const row of group.batches) {
            const batchId = row.batchId?.toString() || null;

            const vId =
              row.vendorId?._id?.toString() ?? row.vendorId?.toString() ?? null;
            // Resolve each saved store to its live reference via phone number
            const liveStores = (row.storesAllocated || [])
              .map((s) =>
                storeRefMap.get(String(s.details?.storeOwnerPhoneNumber ?? "")),
              )
              .filter(Boolean); // drop any that weren't found (should not happen)

            if (!vId) {
              // Preserve the existing batch information even when
              // no vendor is assigned yet.
              if (!bucket.dcBatch) {
                bucket.dcBatch = {
                  batchId: row.batchId || null,
                  batchTitle: row.batchTitle || "",
                  stores: [],
                };
              }

              for (const ref of liveStores) {
                if (!bucket.dcBatch.stores.includes(ref)) {
                  bucket.dcBatch.stores.push(ref);
                }
              }
            } else {
              // Has vendor → merge into vendorBatch for this vendor
              if (!bucket.vendorMap.has(vId)) {
                bucket.vendorMap.set(vId, {
                  vendor: row.vendorId, // populated object from .populate()
                  stores: [],
                  batchTitle: row.batchTitle,
                  batchId: row.batchId,
                });
              }
              const vb = bucket.vendorMap.get(vId);
              for (const ref of liveStores) {
                if (!vb.stores.includes(ref)) vb.stores.push(ref);
              }
            }
          }
        }

        // ── Merge back onto freshBatches ──────────────────────────────────────
        const restored = freshBatches.map((dc) => {
          const bucket = savedByCity.get(dc.cityName);
          if (!bucket) return dc;

          const vendorBatches = [...bucket.vendorMap.values()].filter(
            (vb) => vb.stores.length > 0,
          );

          // FIX 1: was checking bucket.dcStores (always []) instead of
          // bucket.dcBatch.stores where data actually lives after the refactor.
          const restoredStores =
            bucket.dcBatch?.stores?.length > 0
              ? bucket.dcBatch.stores // exact saved subset — respects manual removals
              : dc.stores; // fallback: no save yet, use city-match result

          return {
            ...dc,
            stores: restoredStores,

            ...(bucket.dcBatch
              ? {
                  batchId: bucket.dcBatch.batchId,
                  batchTitle: bucket.dcBatch.batchTitle,
                }
              : {}),

            ...(vendorBatches.length > 0 ? { vendorBatches } : {}),
          };
        });

        // FIX 2: rebuild unmapped stores from scratch based on what was saved.
        // The freshBatches from createDeliveryCenterBatches still has the full
        // city-matched list — a store the user manually moved to unmapped before
        // saving will be in dc.stores AND in pendingStores, causing it to appear
        // in both panels after restore.
        //
        // Collect every store reference that ended up in any restored DC's stores
        // or vendor batches, then anything NOT in that set is truly unmapped.
        const allSavedRefs = new Set();
        for (const dc of restored) {
          dc.stores.forEach((s) => allSavedRefs.add(s));
          (dc.vendorBatches || []).forEach((vb) =>
            vb.stores.forEach((s) => allSavedRefs.add(s)),
          );
        }
        // Expose the recomputed unmapped list via a special key so handleCreateBatches
        // can call setJobsLinkedInBatches with the correct set.
        restored._unmapped = geolocatedStores.filter(
          (s) => !allSavedRefs.has(s),
        );

        return restored;
      } catch (err) {
        console.error("Could not restore saved batches:", err);
        return freshBatches;
      }
    },
    [serverURL, id, token],
  );

  // ── Batch creation ────────────────────────────────────────────────────────
  // Defined BEFORE the effects so the auto-run useEffect below captures the
  // live function reference (const declarations are not hoisted).
  const handleCreateBatches = React.useCallback(async () => {
    const { batches, pendingStores } = createDeliveryCenterBatches(
      deliveryCentersNeeded,
      locationsUploadedInCampaign,
    );
    const restored = await restoreSavedBatches(
      batches,
      locationsUploadedInCampaign,
    );
    // restoreSavedBatches attaches _unmapped when saved data exists.
    // This list correctly excludes stores the user manually moved before saving,
    // unlike pendingStores which comes from createDeliveryCenterBatches and
    // doesn't know about manual moves.
    const unmapped = restored._unmapped ?? pendingStores;
    delete restored._unmapped; // clean up the temporary key before storing in state

    setBatchesCreatedForDeliveryCenter(restored);
    setJobsLinkedInBatches(unmapped);
    setSelectedDCId(null);
  }, [deliveryCentersNeeded, locationsUploadedInCampaign, restoreSavedBatches]);

  // ── Ref flag: tracks whether auto-batch-creation has run this session ─────
  // Using a ref (not state) so flipping it doesn't trigger a re-render,
  // and it won't cause the effect to re-fire with a stale closure.
  const hasAutoCreated = React.useRef(false);

  React.useEffect(() => {
    handleSelectedCampaign();
  }, [id]);

  React.useEffect(() => {
    if (statesInCampaign.length > 0) fetchDeliveryCentersInUploadedStates();
  }, [statesInCampaign]);

  React.useEffect(() => {
    if (
      !hasAutoCreated.current &&
      // geocodeProgress === null &&
      deliveryCentersNeeded.length > 0 &&
      locationsUploadedInCampaign.length > 0
    ) {
      hasAutoCreated.current = true;
      handleCreateBatches();
    }
  }, [
    geocodeProgress,
    deliveryCentersNeeded,
    locationsUploadedInCampaign,
    handleCreateBatches,
  ]);

  React.useEffect(() => {
    // find highest batchId in the saved batches and set it to savedBatches state
    if (batchesCreatedForDeliveryCenter.length > 0) {
      for (const dc of batchesCreatedForDeliveryCenter) {
        if (dc.vendorBatches && dc.vendorBatches.length > 0) {
          const maxBatchLength = Math.max(
            ...dc.vendorBatches.map((vb) => vb.batchTitle.split("-")[1] || 0),
          );
          setSavedBatchesCount(maxBatchLength);
        }
      }
    }
  }, [batchesCreatedForDeliveryCenter]);

  // ── Move store: unmapped → a DC ───────────────────────────────────────────
  const handleMoveToMapped = React.useCallback((store, dcId) => {
    // Use object reference (===) for removal — NOT storeName or any value key.
    // the only safe way to remove exactly the one dragged object.
    setJobsLinkedInBatches((prev) => prev.filter((s) => s !== store));
    setBatchesCreatedForDeliveryCenter((prev) =>
      prev.map((dc) =>
        dc._id === dcId ? { ...dc, stores: [...dc.stores, store] } : dc,
      ),
    );
  }, []);

  // ── Move store: a DC → unmapped ───────────────────────────────────────────
  const handleMoveToUnmapped = React.useCallback((store, dcId) => {
    // Same principle: use reference identity for both the DC removal and the
    // duplicate guard when adding back to unmapped.
    setBatchesCreatedForDeliveryCenter((prev) =>
      prev.map((dc) =>
        dc._id === dcId
          ? { ...dc, stores: dc.stores.filter((s) => s !== store) }
          : dc,
      ),
    );
    setJobsLinkedInBatches((prev) =>
      prev.includes(store) ? prev : [...prev, store],
    );
  }, []);

  // ── Bulk move: multiple unmapped stores → one DC ──────────────────────────
  const handleBulkMoveToMapped = React.useCallback((stores, dcId) => {
    // stores[] is an array of the exact same references that are in
    // jobsNotLinkedinBatches, so a Set of references works correctly here too.
    const refSet = new Set(stores);
    setJobsLinkedInBatches((prev) => prev.filter((s) => !refSet.has(s)));
    setBatchesCreatedForDeliveryCenter((prev) =>
      prev.map((dc) =>
        dc._id === dcId ? { ...dc, stores: [...dc.stores, ...stores] } : dc,
      ),
    );
  }, []);

  //   // ── Save batches to backend ──────────────────────────────────────────────
  //   // Collects the current frontend batch state and POSTs it to the controller.
  //   // Called by a "Save Batch" button in VendorsForSelectedDeliveryCenter.
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSaveBatches = React.useCallback(async () => {
    setIsSaving(true);
    try {
      // Build the payload: one entry per (DC, vendor) combination.
      // Each DC can have multiple vendorBatches (set by the user in VendorsForSelectedDC).
      const batches = [];

      for (const dc of batchesCreatedForDeliveryCenter) {
        const vendorBatches = dc.vendorBatches || [];
        if (dc.stores.length === 0) continue; // skip DCs with no stores at all
        if (vendorBatches.length > 0) {
          // Multi-vendor mode: the user has split stores across vendors
          vendorBatches.forEach((vb) => {
            batches.push({
              deliveryCenterCity: dc.cityName,
              vendorId: vb.vendor?._id ?? vb.vendor?.id ?? null,
              batchTitle: vb.batchTitle,
              batchId: vb.batchId,
              stores: vb.stores,
            });
          });
        } else {
          // Single-batch mode (no vendor assigned yet): save all stores under null vendor
          batches.push({
            deliveryCenterCity: dc.cityName,
            vendorId: null,
            batchTitle: dc.batchTitle,
            stores: dc.stores,
          });
        }
      }
      await axios.post(
        `${serverURL}/deliveryCenter/${id}/dc-batches/save`,
        { batches },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      await handleCreateBatches();
      setSnackbar({
        open: true,
        message: "Batch configuration saved successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error saving batches:", err);
      setSnackbar({
        open: true,
        message: "Failed to save batch configuration.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    batchesCreatedForDeliveryCenter,
    serverURL,
    id,
    token,
    handleCreateBatches,
  ]);

  const handleSnackbarClose = React.useCallback(() => {
    setSnackbar({ ...snackbar, open: false });
  }, [snackbar]);
  // ── Derived: the DC the user clicked "View on Map" for ───────────────────
  const selectedDC = useMemo(
    () =>
      batchesCreatedForDeliveryCenter.find((dc) => dc._id === selectedDCId) ??
      null,
    [batchesCreatedForDeliveryCenter, selectedDCId],
  );
  // ── Geocoded count for the sites table status line ────────────────────────
  const geocodedCount = useMemo(
    () =>
      locationsUploadedInCampaign.filter((l) => toLatLng(l.locationCoordinates))
        .length,
    [locationsUploadedInCampaign],
  );

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ background: "#F5F7FB", padding: 3 }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {selectedCampaign?.campaignTitle}
          </Typography>
          <Typography color="text.secondary">
            Batch & Delivery Centre Configuration
          </Typography>
        </Box>
        <div className="w-30">
          <GlobalButton leftIcon={<AddIcon />} onClick={handleCreateBatches}>
            {`Create Batches`}
          </GlobalButton>
        </div>
      </Box>
      {/* ── Batch Parameters ─────────────────────────────────────────────────── */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight={700} mb={3}>
          Batch Parameters
        </Typography>
        <div className="flex items-center gap-4">
          <Slider
            label="Non Chargeable Delivery Radius"
            unit="KM"
            min={0}
            max={200}
            step={20}
            defaultValue={distance}
            onChange={setDistance}
          />
        </div>
      </Card>

      {/* ── Batches / Unmapped stores ─────────────────────────────────────────── */}
      <Box mt={3}>
        <MappedStoresInCampaign
          savedBatchesCount={savedBatchesCount}
          mappedStores={batchesCreatedForDeliveryCenter}
          unmappedStores={jobsNotLinkedinBatches}
          selectedDCId={selectedDCId}
          selectedDC={selectedDC}
          onSelectDC={setSelectedDCId}
          onMoveToMapped={handleMoveToMapped}
          onMoveToUnmapped={handleMoveToUnmapped}
          onBulkMoveToMapped={handleBulkMoveToMapped}
          geocodeProgress={geocodeProgress}
          selectedMarker={selectedMarker}
          setSelectedMarker={setSelectedMarker}
          setSelectedDCId={setSelectedDCId}
          distance={distance}
          onSaveBatches={handleSaveBatches}
          isSaving={isSaving}
          // Receives (dcId, updatedBatches[]) — the child owns all diff/merge logic
          // and hands back the complete new vendorBatches array to keep this simple.
          onAssignVendorToBatch={(dcId, updatedBatches) => {
            setBatchesCreatedForDeliveryCenter((prev) =>
              prev.map((dc) =>
                dc._id === dcId ? { ...dc, vendorBatches: updatedBatches } : dc,
              ),
            );
          }}
        />
      </Box>
      <GlobalSnackbar snackbar={snackbar} handleClose={handleSnackbarClose} />
    </Box>
  );
};
export default AssignDeliveryCenterInCampaign;
// ─── Column config ────────────────────────────────────────────────────────────
const columns = [
  { field: "srNo", headerName: "S. NO.", width: 20 },
  { field: "storeName", headerName: "STORE NAME", width: 80 },
  { field: "address", headerName: "ADDRESS", width: 250 },
];
// /** Safely extract { lat, lng } — handles { lat,lng } and { latitude,longitude } */
const toLatLng = (coords) => {
  if (!coords) return null;
  const lat = parseFloat(coords.lat ?? coords.latitude);
  const lng = parseFloat(coords.lng ?? coords.longitude);
  if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

const createDeliveryCenterBatches = (deliveryCenters, uploadedStores) => {
  const cityToDC = new Map();
  deliveryCenters.forEach((dc) => {
    cityToDC.set(dc.cityName?.trim().toLowerCase(), dc);
    (dc.linkedCities || []).forEach((city) => {
      cityToDC.set(city.cityName?.trim().toLowerCase(), dc);
    });
  });

  const batches = {};
  deliveryCenters.forEach((dc) => {
    batches[dc._id] = {
      ...dc,
      stores: [],
      batchTitle: `Batch-${Object.keys(batches).length + 1}`,
    };
  });

  const pendingStores = [];
  uploadedStores.forEach((store) => {
    const city = store?.details?.city?.trim().toLowerCase();
    const state = store?.details?.state?.trim().toLowerCase();
    const dc = cityToDC.get(city);
    if (dc?.stateName?.toLowerCase() === state.toLowerCase())
      batches[dc._id].stores.push(store);
    else pendingStores.push(store);
  });

  return { batches: Object.values(batches), pendingStores };
};
